import React, { useState, useEffect, useMemo } from 'react'

// evertel
import { FormInput } from '../../src'
import { usePasswordValidation } from '@evertel/account'
import { IconName } from '@fortawesome/fontawesome-svg-core'

interface PasswordInputProps {
    value: string
    label?: string
    name?: string
    placeholder?: string
    className?: string
    onChange: (value: string) => void
    onValidate?: (value: boolean) => void
    disabled?: boolean
    showPreviousPasswordRequirement?: boolean
    enablePasswordValidation?: boolean
    userData?: {
        firstName?: string
        lastName?: string
        email?: string
    }
    leftIcon?: IconName
    required?: boolean
}

export const PasswordInput: React.FC<PasswordInputProps> = ({
    value,
    label = 'Password',
    name = 'password',
    placeholder = 'Type your password',
    className = '',
    onChange,
    onValidate,
    disabled = false,
    showPreviousPasswordRequirement = true,
    enablePasswordValidation = false,
    userData,
    leftIcon = 'lock',
    required = true
}) => {
    const defaultSuggestions = useMemo(() => {
        const suggestions = ['Password must be a minimum of 20 characters']
        if (showPreviousPasswordRequirement) {
            suggestions.push('Password must not be identical to the previous ten (10) passwords')
        }
        return suggestions
    }, [showPreviousPasswordRequirement])

    // UI State
    const [showPassword, setShowPassword] = useState(false)

    // Use shared validation hook
    const {
        score,
        result,
        warning,
        suggestions,
        isValid,
        validatePassword
    } = usePasswordValidation(userData, defaultSuggestions)

    // Call onValidate when validation state changes
    useEffect(() => {
        if (enablePasswordValidation && onValidate && isValid !== undefined) {
            onValidate(isValid)
        }
    }, [enablePasswordValidation, onValidate, isValid])

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value
        onChange(newPassword)
        if (enablePasswordValidation) {
            validatePassword(newPassword)
        }
    }

    return (
        <div className={`text-start ${className}`}>
            <FormInput
                type={showPassword ? 'text' : 'password'}
                value={value}
                label={label}
                name={name}
                disabled={disabled}
                autoComplete="off"
                placeholder={placeholder}
                required={required}
                onChange={handlePasswordChange}
                isValid={enablePasswordValidation ? isValid : undefined}
                leftIcon={leftIcon}
                rightIcon={showPassword ? 'eye-slash' : 'eye'}
                rightIconProps={{
                    className: 'btn-ghost-muted',
                    tag: 'button',
                    type: 'button',
                    onClick: () => setShowPassword(!showPassword),
                    id: 'password-toggle',
                    'aria-label': showPassword
                        ? 'Hide password'
                        : 'Show password'
                }}
            />
            {enablePasswordValidation && (
                <>
                    {/* Strength Indicator */}
                    {result.message && (
                        <div
                            id={`${name}-error`}
                            className="d-flex p-2 mt-2 mb-n3"
                            aria-live="polite"
                        >
                            <div className={`fw-bold ${
                                result.severity === 'bad' ? 'text-danger' :
                                    result.severity === 'good' ? 'text-info' :
                                        result.severity === 'great' ? 'text-success' : ''
                            }`}>
                                <small>Password Strength: {result.message}</small>
                            </div>
                        </div>
                    )}

                    {/* Requirements Container */}
                    {(warning || suggestions.length > 0) && (
                        <section
                            id={`${name}-feedback`}
                            className="border border-2 rounded p-3 mt-3 fs-5"
                            aria-live="polite"
                        >
                            <h2 className="h6 mb-2">
                                <span className="visually-hidden">Password </span>
                                Requirements:
                            </h2>
                            <ul className="m-0" aria-label="List of password requirements">
                                {warning && (
                                    <li>
                                        <span className="text-danger" role="alert">{warning}</span>
                                    </li>
                                )}
                                {suggestions.map((suggestion, index) => (
                                    <li key={index}>
                                        <span>{suggestion}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </>
            )}
        </div>
    )
}
